import {
  formatToLocaleNumber,
  getIconByName,
  showToastNotification,
} from "src/utils";
import styles from "./TrendAlertItems.module.scss";
import { FC, useCallback, useEffect, useMemo } from "react";
import { Table } from "src/components/Table/Table";
import { RootState, useAppDispatch } from "src/store";
import { fetchTrendAlerts } from "src/store/trendAlerts/trendAlertsSlice";
import { Preloader } from "src/components";
import { useSelector } from "react-redux";
import { selectUser } from "src/store/selectors";
import {
  selectTrendAlertsByTrackerId,
  selectTrendAlertsStatus,
} from "src/store/trendAlerts/trendAlertsSelector";
import { MembersSelect } from "../MembersSelect/MembersSelect";
import { useModal } from "src/hooks";
import { ConfirmDeleteTrendAlertModal } from "src/features/Modals/ConfirmDeleteTrendAlertModal/ConfirmDeleteTrendAlertModal";
import {
  VOLUME_ALERTS_TABLE_COLUMNS,
  NEW_DATA_ALERTS_TABLE_COLUMNS,
} from "../../constants";
import { useTranslation } from "react-i18next";
import {
  filterAlertsByType,
  transformAlertsToModalItems,
  formatVolumeDisplay,
} from "../../utils";

interface TrendAlertItemsProps {
  onEdit: (id: string) => void;
  trackerId: string;
  selectedUserId: string;
  setSelectedUserId: (id: string) => void;
  alertGroup?: "volume" | "newData";
}

export const TrendAlertItems: FC<TrendAlertItemsProps> = ({
  onEdit,
  trackerId,
  selectedUserId,
  setSelectedUserId,
  alertGroup = "volume",
}) => {
  const currentUser = useSelector((state: RootState) => selectUser(state));

  const { setModal } = useModal();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const alerts = useSelector((state: RootState) =>
    selectTrendAlertsByTrackerId(state, trackerId),
  );
  const isLoading = useSelector(
    (state: RootState) => selectTrendAlertsStatus(state) === "loading",
  );

  const handleError = useCallback(
    (key: string, error?: unknown) => {
      console.error(t(key), error);
      showToastNotification({ type: "error", text: t(key) });
    },
    [t],
  );

  useEffect(() => {
    if (!alerts.length && selectedUserId) {
      dispatch(fetchTrendAlerts({ authorId: selectedUserId, trackerId }));
    }
  }, [selectedUserId, trackerId, alerts.length, dispatch]);

  const list = useMemo(() => {
    const filtered = filterAlertsByType(alerts, alertGroup);
    return transformAlertsToModalItems(filtered);
  }, [alerts, alertGroup]);

  const handleDeleteAlert = useCallback(
    async (alertId: string) => {
      setModal(
        "confirm-delete-trend-alert",
        <ConfirmDeleteTrendAlertModal alertId={alertId} />,
      );
    },
    [setModal],
  );

  const handleEditAlert = useCallback(
    async (alert: TrendAlert.CreationData) => {
      try {
        onEdit(alert);
      } catch (error) {
        handleError("component.modal.trend_alerts.error_updating", error);
      }
    },
    [onEdit, handleError],
  );

  const formattedList = useMemo(() => {
    const isEditable = currentUser.id === selectedUserId;

    return list.map((item) => {
      const baseItem = {
        ...item,
        edit: isEditable ? (
          <div className={styles.icons} onClick={() => handleEditAlert(item)}>
            {getIconByName("Pencil")}
          </div>
        ) : (
          <div className={styles.disabled}>{getIconByName("Pencil")}</div>
        ),
        delete: isEditable ? (
          <div
            className={styles.icons}
            onClick={() => handleDeleteAlert(item.id)}
          >
            {getIconByName("TrashOutline")}
          </div>
        ) : (
          <div className={styles.disabled}>{getIconByName("TrashOutline")}</div>
        ),
      };

      if (alertGroup === "newData") {
        baseItem.minVolume = item.minVolume;
      } else {
        baseItem.currentVolume = item.currentVolume
          ? formatToLocaleNumber(item.currentVolume)
          : "-";
        baseItem.targetVolume = item.targetVolume
          ? formatVolumeDisplay(
              item.targetVolume as number,
              item.type,
              item.percentage,
            )
          : "-";
      }

      return baseItem;
    });
  }, [
    currentUser.id,
    selectedUserId,
    list,
    alertGroup,
    handleEditAlert,
    handleDeleteAlert,
  ]);

  return (
    <div className={styles.container}>
      {["admin", "owner"].includes(currentUser.companyRole) && (
        <MembersSelect
          onSelect={setSelectedUserId}
          selectedValue={selectedUserId}
        />
      )}
      {isLoading ? (
        <div className={styles.loader}>
          <Preloader />
        </div>
      ) : (
        <Table
          type="trend-alerts"
          items={formattedList}
          tableColumns={
            alertGroup === "newData"
              ? NEW_DATA_ALERTS_TABLE_COLUMNS
              : VOLUME_ALERTS_TABLE_COLUMNS
          }
          itemsPerPage={2}
          emptyTableLabel={
            selectedUserId === currentUser?.id
              ? t("component.modal.trend_alerts.create_new")
              : t("component.trend_alerts.no_alerts")
          }
          cardHeaderProp={
            alertGroup === "newData"
              ? ["condition"]
              : ["condition", "targetVolume"]
          }
        />
      )}
    </div>
  );
};
